// src/pages/SalesVsExpense.jsx
import React, { useMemo } from "react";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const buildChartData = (dailyRecords, period) => {
    const now = new Date();
    const todayStr = now.toISOString().slice(0, 10);

    // ---- 1. Determine which days belong to the selected period ----
    let relevantDays = [];

    switch (period) {
        case "Today":
            relevantDays = dailyRecords.filter(d => d.date === todayStr);
            break;
        case "Yesterday": {
            const y = new Date(now);
            y.setDate(y.getDate() - 1);
            const yStr = y.toISOString().slice(0, 10);
            relevantDays = dailyRecords.filter(d => d.date === yStr);
            break;
        }
        case "Weekly": {
            const start = new Date(now);
            start.setDate(start.getDate() - 6);               // last 7 days
            relevantDays = dailyRecords.filter(d => new Date(d.date) >= start);
            break;
        }
        case "Monthly": {
            const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
            relevantDays = dailyRecords.filter(d => new Date(d.date) >= monthStart);
            break;
        }
        default:
            // custom range
            if (period?.type === "Custom Date") {
                const { from, to } = period;
                relevantDays = dailyRecords.filter(d => d.date >= from && d.date <= to);
            } else {
                relevantDays = dailyRecords;
            }
    }

    // ---- 2. Group by day (or by month for "Monthly") ----
    const grouped = new Map(); // key = label (e.g. "2025-11-03" or "Nov 2025")

    const formatLabel = (dateStr, isMonthly) => {
        const d = new Date(dateStr);
        if (isMonthly) {
            return d.toLocaleString("default", { month: "short", year: "numeric" });
        }
        // daily / weekly / custom → show short date
        return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
    };

    const isMonthly = period === "Monthly";

    relevantDays.forEach(day => {
        const label = formatLabel(day.date, isMonthly);
        if (!grouped.has(label)) {
            grouped.set(label, { sales: 0, expenses: 0 });
        }
        const rec = grouped.get(label);
        rec.sales += day.sales;
        rec.expenses += day.expenses;
    });

    // Sort chronologically
    const sortedLabels = Array.from(grouped.keys()).sort((a, b) => {
        // For monthly we only have one entry, otherwise compare dates
        if (isMonthly) return 0;
        const da = new Date(a.split(" ")[0] + " " + a.split(" ")[1]); // crude but works for "3 Nov"
        const db = new Date(b.split(" ")[0] + " " + b.split(" ")[1]);
        return da - db;
    });

    const salesData = sortedLabels.map(l => grouped.get(l).sales);
    const expenseData = sortedLabels.map(l => grouped.get(l).expenses);

    return { labels: sortedLabels, sales: salesData, expenses: expenseData };
};

/* ------------------------------------------------------------------ */
/*  Chart options (shared)                                            */
/* ------------------------------------------------------------------ */
const chartOptions = (title) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: { position: "top", labels: { font: { size: 14, weight: "600" }, color: "#333" } },
        title: {
            display: true,
            text: title,
            font: { size: 20, weight: "bold" },
            color: "#222",
            padding: { bottom: 20 },
        },
        tooltip: {
            callbacks: {
                label: ctx => {
                    const val = ctx.raw.toLocaleString("en-IN");
                    return `${ctx.dataset.label}: ₹${val}`;
                },
            },
        },
    },
    scales: {
        y: {
            beginAtZero: true,
            grid: { drawBorder: false, color: "rgba(0,0,0,0.08)" },
            ticks: {
                callback: v => "₹" + v.toLocaleString("en-IN"),
                color: "#444",
            },
        },
        x: { ticks: { color: "#444" }, grid: { display: false } },
    },
});

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */
export default function SalesVsExpense({ filter, dailyRecords }) {
    const chartInfo = useMemo(() => buildChartData(dailyRecords, filter), [dailyRecords, filter]);

    const title = (() => {
        if (filter === "Today") return "Today – Sales vs Expenses (₹)";
        if (filter === "Yesterday") return "Yesterday – Sales vs Expenses (₹)";
        if (filter === "Weekly") return "Last 7 Days – Sales vs Expenses (₹)";
        if (filter === "Monthly") return "Current Month – Sales vs Expenses (₹)";
        if (filter?.type === "Custom Date")
            return `${new Date(filter.from).toLocaleDateString("en-IN", {
                day: "numeric",
                month: "short",
            })} – ${new Date(filter.to).toLocaleDateString("en-IN", {
                day: "numeric",
                month: "short",
                year: "numeric",
            })} – Sales vs Expenses (₹)`;
        return "Sales vs Expenses (₹)";
    })();

    const data = {
        labels: chartInfo.labels,
        datasets: [
            {
                label: "Sales",
                data: chartInfo.sales,
                backgroundColor: "rgba(54, 162, 235, 0.7)",
                borderRadius: 8,
                maxBarThickness: 55,
            },
            {
                label: "Expenses",
                data: chartInfo.expenses,
                backgroundColor: "rgba(255, 159, 64, 0.7)",
                borderRadius: 8,
                maxBarThickness: 55,
            },
        ],
    };

    return (
        <div className="min-h-[400px] h-full">
            <Bar options={chartOptions(title)} data={data} />
        </div>
    );
}